import { useTranslation } from 'react-i18next';
import { ProductSelectionCard } from 'is24-corecss';
import type { FieldElement } from 'react-hook-form';
import type { ADACData, Plan, PlanType } from '@/components/content/welcomeModal/adacUpsell/model';
import { usePriceFormatterHook } from '@/components/content/welcomeModal/adacUpsell/usePriceFormatter.hook';
import styles from './ProductTiles.styles';

type Props = Pick<ADACData, 'plans'> & {
  selectedProduct: PlanType;
  onProuctSelect: (type: PlanType) => void;
} & Pick<FieldElement, 'className'>;

export const ProductTiles = ({ plans, onProuctSelect, selectedProduct, className }: Props) => {
  const { t } = useTranslation();

  return (
    <fieldset className={className}>
      <legend className='screenreader-only'>{t('common.adacModal.plans.common.selection')}</legend>
      <div className='grid grid-flex gutter-l'>
        {plans.map((plan, id) => (
          <ProductTile
            key={plan.id}
            plan={plan}
            previousPlan={id > 0 ? plans[id - 1] : undefined}
            isSelected={selectedProduct === plan.id}
            onSelect={onProuctSelect}
          />
        ))}
      </div>
    </fieldset>
  );
};
const ProductTile = ({
  plan,
  previousPlan,
  isSelected,
  onSelect,
}: {
  plan: Plan;
  previousPlan?: Plan;
  isSelected: boolean;
  onSelect: (id: PlanType) => void;
}) => {
  const { t } = useTranslation();
  const { formatPrice, formatPercent } = usePriceFormatterHook();

  const price = plan.discountedPrice ?? plan.originalPrice;
  const previousPrice = previousPlan?.discountedPrice ?? previousPlan?.originalPrice;
  // Consider other implementation - currently we assume a strict order of smaller to larger
  const savingsComparedToPrevious = previousPrice
    ? Math.round((previousPrice.amount * 12 - price.amount) * 100) / 100
    : undefined;
  const rebate = plan.originalPrice.amount > 0 ? 1 - price.amount / plan.originalPrice.amount : 0;

  const discountText = plan.discountedPrice ? (
    <span className={`${styles.discountBadge}`} data-adac-discount>
      <span className='palm-hide'>{t('common.adacModal.plans.common.rebate', { rebate: formatPercent(rebate) })}</span>
      <span className='lap-hide desk-hide'>−{formatPercent(rebate)}</span>
    </span>
  ) : undefined;

  const savingsLabel =
    savingsComparedToPrevious !== undefined ? (
      <span className={styles.savingsBadge}>
        {t('common.adacModal.plans.yearly.savings', {
          savings: formatPrice({ ...price, amount: savingsComparedToPrevious }),
        })}
      </span>
    ) : undefined;

  return (
    <div className='grid-item palm-one-half lap-one-half desk-one-half'>
      <ProductSelectionCard
        className={`${styles.productTile} height-full cursor-pointer`}
        radio
        name='adac-plan'
        checked={isSelected}
        onChange={() => onSelect(plan.id)}
        onClick={(event) => {
          // Radio changes are reported by onChange; other card clicks select the plan here.
          if (!(event.target instanceof HTMLInputElement)) onSelect(plan.id);
        }}
        title={t(`common.adacModal.plans.${plan.id}.name`)}
        subline={t('common.adacModal.plans.common.minimum-term')}
        price={formatPrice(price)}
        period={t(`common.adacModal.plans.${plan.id}.billing-period`)}
        discountPrice={plan.discountedPrice ? formatPrice(plan.originalPrice) : undefined}
        discountText={discountText}
        label={savingsLabel}
        cta={false}
      />
    </div>
  );
};
